import { useEffect, useRef } from 'react';

/**
 * Layered single-key shortcuts (§7, D-10). Handlers are keyed by `event.key`
 * ("s", "?", "Enter", "Escape", "Shift+Enter"). Keys are ignored while typing in a
 * field except Escape. Layers with a higher priority (picker > panel > view > shell)
 * see the key first; a handler returning `false` passes it on.
 */
export type KeyHandler = (e: KeyboardEvent) => void | boolean;
export type KeyMap = Record<string, KeyHandler>;

interface Layer {
  priority: number;
  map: KeyMap;
}

const layers = new Set<Layer>();
let installed = false;

export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export function keyName(e: KeyboardEvent): string {
  const base = e.key === ' ' ? 'Space' : e.key;
  if (e.key === 'Enter' && e.shiftKey) return 'Shift+Enter';
  if (e.metaKey || e.ctrlKey || e.altKey) return `${e.ctrlKey ? 'Ctrl+' : ''}${e.metaKey ? 'Meta+' : ''}${e.altKey ? 'Alt+' : ''}${base}`;
  return base;
}

function dispatch(e: KeyboardEvent) {
  if (e.defaultPrevented) return;
  const name = keyName(e);
  const typing = isTypingTarget(e.target);
  if (typing && name !== 'Escape') return;
  const sorted = [...layers].sort((a, b) => b.priority - a.priority);
  for (const layer of sorted) {
    const handler = layer.map[name];
    if (!handler) continue;
    const result = handler(e);
    if (result !== false) {
      e.preventDefault();
      return;
    }
  }
}

function install() {
  if (installed) return;
  installed = true;
  window.addEventListener('keydown', dispatch);
}

export function useKeys(map: KeyMap, opts: { priority?: number; enabled?: boolean } = {}): void {
  const ref = useRef<KeyMap>(map);
  ref.current = map;
  const { priority = 0, enabled = true } = opts;
  useEffect(() => {
    if (!enabled) return;
    install();
    // The layer reads the latest map via the ref so handlers never go stale.
    const layer: Layer = {
      priority,
      get map() {
        return ref.current;
      },
    } as Layer;
    layers.add(layer);
    return () => {
      layers.delete(layer);
    };
  }, [priority, enabled]);
}

export const PRIORITY = { shell: 0, view: 10, panel: 20, picker: 30, overlay: 40 } as const;
